import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Switch,
  Slider,
  Collapse,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  styled,
  SelectChangeEvent,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  LocalHospital as DoctorIcon,
  Badge as BadgeIcon,
  School as QualificationIcon,
  Work as WorkIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  MedicalServices as MedicalIcon,
  Description as DocumentIcon,
  PhotoCamera as CameraIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Warning as WarningIcon,
  Help as HelpIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  KeyboardArrowRight as ArrowRightIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Styled Components
const GradientBackground = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #E8E8E8 0%, #F5F5F5 50%, #EBEBEB 100%)',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
  position: 'relative',
  zIndex: 10,
}));

const PremiumTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: '#CBD5E1',
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: '#94A3B8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2563EB',
      borderWidth: 2,
    },
    '& input': {
      cursor: 'text !important',
    },
    '& textarea': {
      cursor: 'text !important',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#2563EB',
    },
  },
}));

const PremiumButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '12px 32px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(37, 99, 235, 0.35)',
  },
}));

// Interfaces
interface FormData {
  // Personal Information
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  mobile: string;
  alternateMobile: string;
  email: string;
  profilePhoto: File | null;
  profilePhotoPreview: string;

  // Professional Details
  qualification: string;
  otherQualification: string;
  specialization: string;
  otherSpecialization: string;
  totalExperience: string;
  governmentExperience: string;
  professionalSummary: string;
  expertise: string[];
  clinicalInterests: string;
  specialInterests: string;

  // Government Service Details
  hospitalName: string;
  department: string;
  designation: string;
  employeeId: string;
  joiningDate: string;
  hospitalAddress: string;
  city: string;
  district: string;
  state: string;

  // Medical Registration
  registrationNumber: string;
  registrationDate: string;
  validTill: string;
  licenseNumber: string;
  issuingAuthority: string;
  certifications: string[];
  otherCertifications: string;

  // Availability
  workingDays: string[];
  shiftStartTime: string;
  shiftEndTime: string;
  homeVisitAvailable: boolean;
  emergencyAvailable: boolean;
  maxAppointmentsPerDay: number;
  travelBufferTime: number;
  consultationFee: string;
  visitFee: string;

  // Documents
  documents: {
    passportPhoto: File | null;
    aadhaarCard: File | null;
    panCard: File | null;
    mbbsCertificate: File | null;
    qualificationCertificates: File | null;
    medicalRegistrationCertificate: File | null;
    governmentIdCard: File | null;
  };

  // Terms
  declaration: boolean;

  // Password
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  [key: string]: string;
}

// Constants
const STEPS = [
  { label: 'Personal', icon: <PersonIcon />, description: 'Personal Information' },
  { label: 'Professional', icon: <MedicalIcon />, description: 'Professional Details' },
  { label: 'Employment', icon: <BusinessIcon />, description: 'Government Service' },
  { label: 'Registration', icon: <VerifiedIcon />, description: 'Medical Council' },
  { label: 'Availability', icon: <TimeIcon />, description: 'Schedule' },
  { label: 'Documents', icon: <DocumentIcon />, description: 'Upload Documents' },
  { label: 'Review', icon: <CheckIcon />, description: 'Review & Submit' },
];

const QUALIFICATIONS = ['MBBS', 'MD', 'MS', 'DNB', 'DM', 'MCh', 'Other'];
const SPECIALIZATIONS = [
  'General Medicine', 'Cardiology', 'Orthopedics', 'Pediatrics', 'Neurology',
  'Dermatology', 'Psychiatry', 'Obstetrics & Gynecology', 'Ophthalmology',
  'ENT', 'Radiology', 'Anesthesiology', 'Emergency Medicine', 'Other'
];
const CERTIFICATIONS = ['ACLS', 'BLS', 'Emergency Medicine', 'Critical Care', 'PALS', 'ATLS'];
const WORKING_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
  'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi'
];

/**
 * Premium Doctor Registration Page
 */
const DoctorRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [saveDraft, setSaveDraft] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: string }>({});

  const [formData, setFormData] = useState<FormData>({
    firstName: '', middleName: '', lastName: '', gender: '', dateOfBirth: '',
    mobile: '', alternateMobile: '', email: '', profilePhoto: null, profilePhotoPreview: '',
    qualification: '', otherQualification: '', specialization: '', otherSpecialization: '',
    totalExperience: '', governmentExperience: '', professionalSummary: '',
    expertise: [], clinicalInterests: '', specialInterests: '',
    hospitalName: '', department: '', designation: '', employeeId: '', joiningDate: '',
    hospitalAddress: '', city: '', district: '', state: '',
    registrationNumber: '', registrationDate: '', validTill: '', licenseNumber: '', issuingAuthority: '',
    certifications: [], otherCertifications: '',
    workingDays: [], shiftStartTime: '09:00', shiftEndTime: '17:00',
    homeVisitAvailable: false, emergencyAvailable: false,
    maxAppointmentsPerDay: 10, travelBufferTime: 30,
    consultationFee: '', visitFee: '',
    documents: {
      passportPhoto: null, aadhaarCard: null, panCard: null,
      mbbsCertificate: null, qualificationCertificates: null,
      medicalRegistrationCertificate: null, governmentIdCard: null,
    },
    declaration: false,
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Calculate completion percentage
  const calculateCompletion = () => {
    const fields = [
      formData.firstName, formData.lastName, formData.gender, formData.dateOfBirth,
      formData.mobile, formData.email,
      formData.qualification, formData.specialization, formData.totalExperience,
      formData.hospitalName, formData.department, formData.designation, formData.employeeId,
      formData.registrationNumber, formData.registrationDate, formData.validTill,
      formData.workingDays.length > 0,
    ];
    const filledFields = fields.filter(Boolean).length;
    return Math.round((filledFields / fields.length) * 100);
  };

  const completionPercentage = calculateCompletion();

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 0) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.gender) newErrors.gender = 'Gender is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
      else if (!/^[6-9]\d{9}$/.test(formData.mobile)) newErrors.mobile = 'Invalid mobile number';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      // Password validation
      if (!formData.password.trim()) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.password)) {
        newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
      }
      if (!formData.confirmPassword.trim()) newErrors.confirmPassword = 'Confirm password is required';
      else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }

    if (step === 1) {
      if (!formData.qualification) newErrors.qualification = 'Qualification is required';
      if (!formData.specialization) newErrors.specialization = 'Specialization is required';
      if (!formData.totalExperience) newErrors.totalExperience = 'Experience is required';
    }

    if (step === 2) {
      if (!formData.hospitalName.trim()) newErrors.hospitalName = 'Hospital name is required';
      if (!formData.department.trim()) newErrors.department = 'Department is required';
      if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
      if (!formData.employeeId.trim()) newErrors.employeeId = 'Employee ID is required';
    }

    if (step === 3) {
      if (!formData.registrationNumber.trim()) newErrors.registrationNumber = 'Registration number is required';
      if (!formData.registrationDate) newErrors.registrationDate = 'Registration date is required';
      if (!formData.validTill) newErrors.validTill = 'Validity date is required';
    }

    if (step === 4) {
      if (formData.workingDays.length === 0) newErrors.workingDays = 'Select at least one working day';
    }

    if (step === 5) {
      if (!formData.documents.passportPhoto) newErrors.passportPhoto = 'Passport photo is required';
      if (!formData.documents.aadhaarCard) newErrors.aadhaarCard = 'Aadhaar card is required';
      if (!formData.documents.panCard) newErrors.panCard = 'PAN card is required';
      if (!formData.documents.mbbsCertificate) newErrors.mbbsCertificate = 'MBBS certificate is required';
    }

    if (step === 6) {
      if (!formData.declaration) newErrors.declaration = 'Please accept the declaration';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleMultiSelectChange = (name: string, value: string) => {
    setFormData((prev) => {
      const currentValues = (prev as any)[name] as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return { ...prev, [name]: newValues };
    });
  };

  const handleFileUpload = (name: string, file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedFiles((prev) => ({ ...prev, [name]: reader.result as string }));
      };
      reader.readAsDataURL(file);

      setFormData((prev) => ({
        ...prev,
        documents: { ...prev.documents, [name]: file },
      }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const checkAvailability = async (field: string, value: string) => {
    if (!value.trim()) return;
    setChecking((prev) => ({ ...prev, [field]: true }));
    try {
      const endpoints: { [key: string]: string } = {
        mobile: `/api/v1/registration/check-mobile/${value}`,
        email: `/api/v1/registration/check-email/${value}`,
        employeeId: `/api/v1/registration/check-employee-id/${value}`,
        registrationNumber: `/api/v1/registration/check-registration-number/${value}`,
      };
      if (!endpoints[field]) return;
      const response = await axios.get(`${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}${endpoints[field]}`);
      if (!response.data.data) {
        const messages: { [key: string]: string } = {
          mobile: 'This mobile number is already registered',
          email: 'This email is already registered',
          employeeId: 'This Employee ID is already registered',
          registrationNumber: 'This registration number is already registered',
        };
        setErrors((prev) => ({ ...prev, [field]: messages[field] || `This ${field} is already taken` }));
      }
    } catch (err) {
      console.error('Error checking availability:', err);
    } finally {
      setChecking((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;
    setLoading(true);
    setError('');

    try {
      // Submit to backend API
      const payload = {
        // Personal
        username: formData.email.split('@')[0],
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        fullName: `${formData.firstName} ${formData.middleName} ${formData.lastName}`.trim(),
        // Professional
        registrationNumber: formData.registrationNumber,
        qualification: formData.qualification,
        specialization: formData.specialization,
        department: formData.department,
        experienceYears: parseInt(formData.totalExperience) || 0,
        consultationFee: parseFloat(formData.consultationFee) || 0,
        visitFee: parseFloat(formData.visitFee) || 0,
        bio: formData.professionalSummary,
      };

      const response = await axios.post(`${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/registration/doctor`, payload);

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success Screen
  if (success) {
    return (
      <GradientBackground>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <GlassCard sx={{ p: 6, maxWidth: 500, textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckIcon sx={{ fontSize: 50, color: 'white' }} />
              </Box>
            </motion.div>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#0F172A' }}>
              Registration Submitted!
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', mb: 3 }}>
              Your application has been submitted successfully.
            </Typography>
            <Box sx={{ bgcolor: '#FEF3C7', borderRadius: 2, p: 2, mb: 3, border: '1px solid #F59E0B' }}>
              <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600 }}>
                ⏳ Pending Admin Approval
              </Typography>
              <Typography variant="body2" sx={{ color: '#B45309', mt: 1 }}>
                Please wait for the administrator to approve your account. You will be able to login after approval.
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#94A3B8', mb: 4 }}>
              You will receive an email and SMS notification once your account is approved.
            </Typography>
            <PremiumButton
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate('/login')}
              sx={{
                background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                color: 'white',
              }}
            >
              Return to Login
            </PremiumButton>
          </GlassCard>
        </motion.div>
      </GradientBackground>
    );
  }

  // Left Panel - Branding
  const LeftPanel = () => (
    <Box
      sx={{
        width: { xs: '100%', md: '35%' },
        minHeight: { xs: 'auto', md: '100vh' },
        background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 50%, #FFFBEB 100%)',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 40%)',
          background2: 'radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 40%)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #F59E0B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <DoctorIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
            Civilhealth Healthcare
          </Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8' }}>
            Government Healthcare Network
          </Typography>
        </Box>

        {/* Main Message */}
        <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mb: 2, lineHeight: 1.2 }}>
          Join Our Network of Healthcare Professionals
        </Typography>
        <Typography variant="body1" sx={{ color: '#94A3B8', mb: 4, maxWidth: 400, mx: 'auto' }}>
          Provide quality healthcare services to senior government officers and their families across India.
        </Typography>

        {/* Features */}
        <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
          {[
            { icon: <VerifiedIcon />, text: 'Secure Verification Process' },
            { icon: <SecurityIcon />, text: 'Professional Credential Validation' },
            { icon: <TimeIcon />, text: 'Flexible Scheduling Options' },
            { icon: <StarIcon />, text: 'Government Healthcare Network' },
          ].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ color: '#10B981' }}>{feature.icon}</Box>
                <Typography variant="body2" sx={{ color: '#E2E8F0' }}>{feature.text}</Typography>
              </Box>
            </motion.div>
          ))}
        </Box>

        {/* Progress Indicator */}
        <Box sx={{ mt: 6, width: '100%', maxWidth: 300, mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748B' }}>Registration Progress</Typography>
            <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600 }}>{completionPercentage}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#E5E7EB',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                borderRadius: 4,
              },
            }}
          />
        </Box>
      </motion.div>
    </Box>
  );

  // Render Step Content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A' }}>
              Personal Information
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Enter your personal details as per your official documents
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: 'transparent' ,
                        border: '3px solid #2563EB',
                      }}
                      src={formData.profilePhotoPreview}
                    >
                      <PersonIcon sx={{ fontSize: 50, color: '#94A3B8' }} />
                    </Avatar>
                    <IconButton
                      component="label"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        bgcolor: 'transparent' ,
                        '&:hover': { bgcolor: 'transparent' },
                      }}
                    >
                      <CameraIcon sx={{ fontSize: 18, color: 'white' }} />
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setFormData((prev) => ({
                              ...prev,
                              profilePhoto: file,
                              profilePhotoPreview: URL.createObjectURL(file),
                            }));
                          }
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <PremiumTextField
                  fullWidth
                  label="First Name *"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <PremiumTextField
                  fullWidth
                  label="Middle Name"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <PremiumTextField
                  fullWidth
                  label="Last Name *"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.gender}>
                  <InputLabel>Gender *</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleSelectChange}
                    label="Gender *"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="MALE">Male</MenuItem>
                    <MenuItem value="FEMALE">Female</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                  {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Date of Birth *"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Mobile Number *"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  onBlur={() => checkAvailability('mobile', formData.mobile)}
                  error={!!errors.mobile}
                  helperText={errors.mobile || '10 digit mobile number'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: checking.mobile && <CircularProgress size={20} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Alternate Mobile"
                  name="alternateMobile"
                  value={formData.alternateMobile}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <PremiumTextField
                  fullWidth
                  label="Email Address *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => checkAvailability('email', formData.email)}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: checking.email && <CircularProgress size={20} />,
                  }}
                />
              </Grid>

              {/* Password Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#64748B', fontWeight: 600 }}>
                  Set Password
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Password *"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password || 'Min 8 chars, uppercase, lowercase, number & special char'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Confirm Password *"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A' }}>
              Professional Information
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Your medical qualifications and professional expertise
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.qualification}>
                  <InputLabel>Highest Qualification *</InputLabel>
                  <Select
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleSelectChange}
                    label="Highest Qualification *"
                    sx={{ borderRadius: 2 }}
                  >
                    {QUALIFICATIONS.map((q) => (
                      <MenuItem key={q} value={q}>{q}</MenuItem>
                    ))}
                  </Select>
                  {errors.qualification && <FormHelperText>{errors.qualification}</FormHelperText>}
                </FormControl>
              </Grid>

              {formData.qualification === 'Other' && (
                <Grid item xs={12} md={6}>
                  <PremiumTextField
                    fullWidth
                    label="Specify Qualification"
                    name="otherQualification"
                    value={formData.otherQualification}
                    onChange={handleChange}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.specialization}>
                  <InputLabel>Specialization *</InputLabel>
                  <Select
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleSelectChange}
                    label="Specialization *"
                    sx={{ borderRadius: 2 }}
                  >
                    {SPECIALIZATIONS.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                  {errors.specialization && <FormHelperText>{errors.specialization}</FormHelperText>}
                </FormControl>
              </Grid>

              {formData.specialization === 'Other' && (
                <Grid item xs={12} md={6}>
                  <PremiumTextField
                    fullWidth
                    label="Specify Specialization"
                    name="otherSpecialization"
                    value={formData.otherSpecialization}
                    onChange={handleChange}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Total Experience (Years) *"
                  name="totalExperience"
                  type="number"
                  value={formData.totalExperience}
                  onChange={handleChange}
                  error={!!errors.totalExperience}
                  helperText={errors.totalExperience}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><QualificationIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 50 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Government Experience (Years)"
                  name="governmentExperience"
                  type="number"
                  value={formData.governmentExperience}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><WorkIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                  inputProps={{ min: 0, max: 50 }}
                />
              </Grid>

              <Grid item xs={12}>
                <PremiumTextField
                  fullWidth
                  label="Professional Summary"
                  name="professionalSummary"
                  multiline
                  rows={4}
                  value={formData.professionalSummary}
                  onChange={handleChange}
                  helperText="Brief description of your expertise and experience"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#64748B' }}>
                  Areas of Expertise
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['General Medicine', 'Critical Care', 'Emergency Medicine', 'Diabetes', 'Hypertension', 'Cardiology'].map((exp) => (
                    <Chip
                      key={exp}
                      label={exp}
                      onClick={() => handleMultiSelectChange('expertise', exp)}
                      color={formData.expertise.includes(exp) ? 'primary' : 'default'}
                      variant={formData.expertise.includes(exp) ? 'filled' : 'outlined'}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A' }}>
              Government Employment Details
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Your current government hospital/organization details
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Hospital/Organization Name *"
                  name="hospitalName"
                  value={formData.hospitalName}
                  onChange={handleChange}
                  error={!!errors.hospitalName}
                  helperText={errors.hospitalName}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Department *"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  error={!!errors.department}
                  helperText={errors.department}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><WorkIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Designation *"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  error={!!errors.designation}
                  helperText={errors.designation || 'e.g., Senior Medical Officer'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Employee ID *"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleChange}
                  onBlur={() => checkAvailability('employeeId', formData.employeeId)}
                  error={!!errors.employeeId}
                  helperText={errors.employeeId || 'Your official government employee ID'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: checking.employeeId && <CircularProgress size={20} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Joining Date"
                  name="joiningDate"
                  type="date"
                  value={formData.joiningDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#64748B' }}>
                  Hospital Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <PremiumTextField
                  fullWidth
                  label="Hospital Address"
                  name="hospitalAddress"
                  value={formData.hospitalAddress}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocationIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <PremiumTextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <PremiumTextField
                  fullWidth
                  label="District"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    name="state"
                    value={formData.state}
                    onChange={handleSelectChange}
                    label="State"
                    sx={{ borderRadius: 2 }}
                  >
                    {INDIAN_STATES.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A' }}>
              Medical Council Registration
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Your medical council registration and license details
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Registration Number *"
                  name="registrationNumber"
                  value={formData.registrationNumber}
                  onChange={handleChange}
                  onBlur={() => checkAvailability('registrationNumber', formData.registrationNumber)}
                  error={!!errors.registrationNumber}
                  helperText={errors.registrationNumber || 'e.g., MCI-123456'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><VerifiedIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    endAdornment: checking.registrationNumber && <CircularProgress size={20} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="License Number"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  helperText="Medical license number"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Registration Date *"
                  name="registrationDate"
                  type="date"
                  value={formData.registrationDate}
                  onChange={handleChange}
                  error={!!errors.registrationDate}
                  helperText={errors.registrationDate}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Valid Till *"
                  name="validTill"
                  type="date"
                  value={formData.validTill}
                  onChange={handleChange}
                  error={!!errors.validTill}
                  helperText={errors.validTill}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <PremiumTextField
                  fullWidth
                  label="Issuing Authority"
                  name="issuingAuthority"
                  value={formData.issuingAuthority}
                  onChange={handleChange}
                  helperText="e.g., Medical Council of India, State Medical Council"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#64748B' }}>
                  Additional Certifications
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {CERTIFICATIONS.map((cert) => (
                    <Chip
                      key={cert}
                      label={cert}
                      onClick={() => handleMultiSelectChange('certifications', cert)}
                      color={formData.certifications.includes(cert) ? 'primary' : 'default'}
                      variant={formData.certifications.includes(cert) ? 'filled' : 'outlined'}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A' }}>
              Availability Configuration
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Set your working hours and availability preferences
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#0F172A' }}>
                  Working Days *
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {WORKING_DAYS.map((day) => (
                    <Chip
                      key={day}
                      label={day.slice(0, 3)}
                      onClick={() => handleMultiSelectChange('workingDays', day)}
                      color={formData.workingDays.includes(day) ? 'primary' : 'default'}
                      variant={formData.workingDays.includes(day) ? 'filled' : 'outlined'}
                      sx={{
                        borderRadius: 2,
                        minWidth: 70,
                        fontWeight: formData.workingDays.includes(day) ? 600 : 400,
                      }}
                    />
                  ))}
                </Box>
                {errors.workingDays && (
                  <Typography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
                    {errors.workingDays}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#0F172A' }}>
                  Shift Start Time
                </Typography>
                <PremiumTextField
                  fullWidth
                  type="time"
                  name="shiftStartTime"
                  value={formData.shiftStartTime}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><TimeIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#0F172A' }}>
                  Shift End Time
                </Typography>
                <PremiumTextField
                  fullWidth
                  type="time"
                  name="shiftEndTime"
                  value={formData.shiftEndTime}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><TimeIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.homeVisitAvailable}
                      onChange={handleCheckboxChange}
                      name="homeVisitAvailable"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>Home Visit Available</Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>Available for home visits</Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.emergencyAvailable}
                      onChange={handleCheckboxChange}
                      name="emergencyAvailable"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>Emergency Services</Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>Available for emergency calls</Typography>
                    </Box>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#0F172A' }}>
                  Maximum Appointments Per Day: {formData.maxAppointmentsPerDay}
                </Typography>
                <Slider
                  value={formData.maxAppointmentsPerDay}
                  onChange={(_, value) => setFormData((prev) => ({ ...prev, maxAppointmentsPerDay: value as number }))}
                  min={1}
                  max={50}
                  marks={[
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 30, label: '30' },
                    { value: 40, label: '40' },
                    { value: 50, label: '50' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Consultation Fee (₹)"
                  name="consultationFee"
                  type="number"
                  value={formData.consultationFee}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <PremiumTextField
                  fullWidth
                  label="Home Visit Fee (₹)"
                  name="visitFee"
                  type="number"
                  value={formData.visitFee}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A' }}>
              Document Upload Center
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Upload required documents for verification
            </Typography>

            <Grid container spacing={2.5}>
              {[
                { key: 'passportPhoto', label: 'Passport Photo', required: true, icon: <CameraIcon /> },
                { key: 'aadhaarCard', label: 'Aadhaar Card', required: true, icon: <DocumentIcon /> },
                { key: 'panCard', label: 'PAN Card', required: true, icon: <DocumentIcon /> },
                { key: 'mbbsCertificate', label: 'MBBS Certificate', required: true, icon: <VerifiedIcon /> },
                { key: 'qualificationCertificates', label: 'Higher Qualification Certificates', required: false, icon: <QualificationIcon /> },
                { key: 'medicalRegistrationCertificate', label: 'Medical Registration Certificate', required: true, icon: <MedicalIcon /> },
                { key: 'governmentIdCard', label: 'Government ID Card', required: true, icon: <BadgeIcon /> },
              ].map((doc) => (
                <Grid item xs={12} md={6} key={doc.key}>
                  <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box sx={{ color: '#2563EB' }}>{doc.icon}</Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {doc.label}
                          {doc.required && <Typography component="span" sx={{ color: 'error.main' }}> *</Typography>}
                        </Typography>
                      </Box>

                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<CloudUploadIcon />}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          borderStyle: 'dashed',
                          borderColor: uploadedFiles[doc.key] ? '#10B981' : '#CBD5E1',
                          bgcolor: uploadedFiles[doc.key] ? '#F0FDF4' : '#F8FAFC',
                        }}
                      >
                        {uploadedFiles[doc.key] ? 'Change File' : 'Upload File'}
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(doc.key, file);
                          }}
                        />
                      </Button>

                      {uploadedFiles[doc.key] && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#10B981' }} />
                          <Typography variant="caption" sx={{ color: '#10B981' }}>
                            File uploaded successfully
                          </Typography>
                        </Box>
                      )}

                      {errors[doc.key] && (
                        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
                          {errors[doc.key]}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Accepted formats:</strong> PDF, JPG, PNG (Max size: 10MB per file)
              </Typography>
            </Alert>
          </motion.div>
        );

      case 6:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A' }}>
              Review & Submit
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
              Please review your information before submitting
            </Typography>

            <Grid container spacing={3}>
              {/* Personal Info Summary */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        Personal Information
                      </Typography>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setActiveStep(0)}>
                        Edit
                      </Button>
                    </Box>
                    <List dense disablePadding>
                      <ListItem disableGutters><ListItemText primary="Name" secondary={`${formData.firstName} ${formData.middleName} ${formData.lastName}`} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Mobile" secondary={formData.mobile} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Email" secondary={formData.email} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Gender" secondary={formData.gender} /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Professional Info Summary */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        Professional Details
                      </Typography>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setActiveStep(1)}>
                        Edit
                      </Button>
                    </Box>
                    <List dense disablePadding>
                      <ListItem disableGutters><ListItemText primary="Qualification" secondary={formData.qualification} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Specialization" secondary={formData.specialization} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Experience" secondary={`${formData.totalExperience} years`} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Expertise" secondary={formData.expertise.join(', ') || 'N/A'} /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Employment Summary */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        Employment Details
                      </Typography>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setActiveStep(2)}>
                        Edit
                      </Button>
                    </Box>
                    <List dense disablePadding>
                      <ListItem disableGutters><ListItemText primary="Hospital" secondary={formData.hospitalName} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Department" secondary={formData.department} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Designation" secondary={formData.designation} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Employee ID" secondary={formData.employeeId} /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Medical Registration Summary */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        Medical Registration
                      </Typography>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setActiveStep(3)}>
                        Edit
                      </Button>
                    </Box>
                    <List dense disablePadding>
                      <ListItem disableGutters><ListItemText primary="Registration No" secondary={formData.registrationNumber} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Valid Till" secondary={formData.validTill} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Certifications" secondary={formData.certifications.join(', ') || 'None'} /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Availability Summary */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        Availability
                      </Typography>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setActiveStep(4)}>
                        Edit
                      </Button>
                    </Box>
                    <List dense disablePadding>
                      <ListItem disableGutters><ListItemText primary="Working Days" secondary={formData.workingDays.join(', ')} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Shift Timing" secondary={`${formData.shiftStartTime} - ${formData.shiftEndTime}`} /></ListItem>
                      <ListItem disableGutters><ListItemText primary="Max Appointments" secondary={formData.maxAppointmentsPerDay} /></ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Documents Summary */}
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E2E8F0', height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        Documents
                      </Typography>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => setActiveStep(5)}>
                        Edit
                      </Button>
                    </Box>
                    <List dense disablePadding>
                      {Object.entries(formData.documents).map(([key, file]) => (
                        <ListItem key={key} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {file ? <CheckCircleOutlineIcon sx={{ color: '#10B981', fontSize: 20 }} /> : <ErrorOutlineIcon sx={{ color: '#EF4444', fontSize: 20 }} />}
                          </ListItemIcon>
                          <ListItemText
                            primary={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            secondary={file ? 'Uploaded' : 'Not uploaded'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.declaration}
                  onChange={handleCheckboxChange}
                  name="declaration"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  I hereby declare that all the information provided above is true and accurate to the best of my knowledge.
                  I understand that providing false information may result in rejection of my application or termination of services.
                </Typography>
              }
              sx={{ mt: 3 }}
            />
            {errors.declaration && (
              <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
                {errors.declaration}
              </Typography>
            )}
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <GradientBackground>
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        <LeftPanel />

        {/* Right Side - Form */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 4 } }}>
          <Box sx={{ width: '100%', maxWidth: 900 }}>
            {/* Back Button */}
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<BackIcon />}
                onClick={() => navigate('/register')}
                sx={{
                  color: '#64748B',
                  '&:hover': {
                    color: '#2563EB',
                    bgcolor: '#2563EB08',
                  },
                }}
              >
                Back to Role Selection
              </Button>
            </Box>

            {/* Progress Bar - Mobile */}
            {isMobile && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>Step {activeStep + 1} of {STEPS.length}</Typography>
                  <Typography variant="body2" sx={{ color: '#10B981', fontWeight: 600 }}>{completionPercentage}% Complete</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(activeStep / (STEPS.length - 1)) * 100}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}

            <GlassCard sx={{ p: { xs: 3, md: 5 } }}>
              {/* Stepper */}
              <Box sx={{ mb: 4, display: { xs: 'none', md: 'block' } }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {STEPS.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: index <= activeStep ? '#2563EB' : '#E2E8F0',
                              color: index <= activeStep ? 'white' : '#94A3B8',
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {index < activeStep ? <CheckIcon sx={{ fontSize: 20 }} /> : step.icon}
                          </Box>
                        )}
                      >
                        <Typography variant="body2" sx={{ fontWeight: index === activeStep ? 600 : 400, color: index === activeStep ? '#0F172A' : '#64748B' }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <AnimatePresence mode="wait">
                {renderStepContent()}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 3, borderTop: '1px solid #E2E8F0' }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  startIcon={<BackIcon />}
                  sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
                >
                  Back
                </Button>

                {activeStep < STEPS.length - 1 ? (
                  <PremiumButton
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NextIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    }}
                  >
                    Continue
                  </PremiumButton>
                ) : (
                  <PremiumButton
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !formData.declaration}
                    sx={{
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Application'}
                  </PremiumButton>
                )}
              </Box>

              {/* Footer */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  Already have an account?{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/login')}
                    sx={{ color: '#2563EB', textTransform: 'none', fontWeight: 600, p: 0, minWidth: 'auto' }}
                  >
                    Login here
                  </Button>
                </Typography>
              </Box>
            </GlassCard>
          </Box>
        </Box>
      </Box>
    </GradientBackground>
  );
};

export default DoctorRegistrationPage;
